import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { BotaoAdicionar } from "./BotaoAdicionar";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { CatalogItemRow } from "./CatalogItemRow";
import { CatalogoBusca, combina } from "./CatalogoBusca";
import { splitInHalf } from "./shade-groups";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";
const inputCls =
  "h-8 min-w-0 flex-1 rounded border border-zinc-200 bg-white px-2 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

type Oferta = { id: string; label: string; active: boolean };

/**
 * A aba Materiais do catálogo: o cardápio.
 *
 * O cadastro de insumos morava aqui e foi pro Estoque em 30/09 — preço de compra, fornecedor,
 * unidade e estoque mínimo são campos de quem compra, não de quem vende. O que sobrou é a lista
 * que o dentista vê ao pedir, que é catálogo de verdade: dela penduram o preço do serviço e a
 * ficha técnica.
 *
 * Mesmo desenho de Serviços: duas colunas, clicar no nome edita, sem lápis. Aqui editar é
 * renomear no lugar, porque uma oferta é só um nome — abrir um diálogo pra um campo seria pedir
 * dois cliques por nada.
 */
export function CardapioSection() {
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const [criando, setCriando] = useState(false);
  const [daBase, setDaBase] = useState(false);
  const [nome, setNome] = useState("");
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nomeEditado, setNomeEditado] = useState("");

  const listaQuery = trpc.prosthesis.catalog.materialOptions.useQuery();
  const ofertas = (listaQuery.data ?? []) as Oferta[];
  const filtradas = ofertas.filter((o) => combina(o.label, busca));
  const [esquerda, direita] = splitInHalf(filtradas);

  const invalidarTudo = async () => {
    await utils.prosthesis.catalog.materialOptions.invalidate();
    // O cardápio aparece no serviço, na tabela de preços e no pedido do dentista.
    utils.prosthesis.catalog.myServiceCategories.invalidate();
    utils.labPricing.invalidate();
    utils.fichaTecnica.invalidate();
  };

  const salvar = trpc.prosthesis.catalog.salvarMaterialOption.useMutation({
    onSuccess: async () => {
      await invalidarTudo();
      setNome("");
      setCriando(false);
      setEditandoId(null);
      toast.success("Cardápio atualizado.");
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar material."),
  });

  const setAtiva = trpc.prosthesis.catalog.setMaterialOptionAtiva.useMutation({
    onSuccess: invalidarTudo,
    onError: (e) => toast.error(e.message || "Erro ao atualizar material."),
  });

  const remover = trpc.prosthesis.catalog.removerMaterialOption.useMutation({
    onSuccess: async () => {
      await invalidarTudo();
      toast.success("Material removido do cardápio.");
    },
    onError: (e) => toast.error(e.message || "Erro ao remover material."),
  });

  const ocupado = salvar.isPending || setAtiva.isPending || remover.isPending;

  const coluna = (itens: Oferta[], chave: string) => (
    <div key={chave} className="rounded-lg border border-zinc-100 p-3 dark:border-border/60">
      {itens.length === 0 ? (
        <p className="px-2 py-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
          {busca.trim() ? "Nada aqui com essa busca." : "Nenhum material ainda."}
        </p>
      ) : (
        itens.map((o) =>
          editandoId === o.id ? (
            <div key={o.id} className="flex items-center gap-1.5 px-2 py-1">
              <input
                value={nomeEditado}
                onChange={(e) => setNomeEditado(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && nomeEditado.trim().length >= 2) {
                    salvar.mutate({ id: o.id, label: nomeEditado.trim() });
                  }
                  if (e.key === "Escape") setEditandoId(null);
                }}
                aria-label={`Novo nome de ${o.label}`}
                autoFocus
                className={inputCls}
              />
              <button
                type="button"
                disabled={nomeEditado.trim().length < 2 || ocupado}
                onClick={() => salvar.mutate({ id: o.id, label: nomeEditado.trim() })}
                aria-label="Salvar nome"
                className="shrink-0 text-brand-700 disabled:opacity-40 dark:text-primary"
              >
                {salvar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setEditandoId(null)}
                aria-label="Cancelar"
                className="shrink-0 text-zinc-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <CatalogItemRow
              key={o.id}
              label={o.label}
              enabled={o.active}
              disabled={ocupado}
              onToggle={(ativa) => setAtiva.mutate({ materialOptionId: o.id, ativa })}
              onEdit={() => {
                setEditandoId(o.id);
                setNomeEditado(o.label);
              }}
              onDelete={() => {
                if (window.confirm(`Remover "${o.label}" do cardápio?`)) {
                  remover.mutate({ materialOptionId: o.id });
                }
              }}
            />
          ),
        )
      )}
    </div>
  );

  return (
    <div id="materiais" className={cardCls}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-tour="catalogo-gerenciar-secao-titulo" className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Materiais
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            O que o dentista escolhe ao montar um pedido — só os ativos aparecem. É a chave do preço
            e da ficha técnica; seus insumos e preços de compra ficam em Estoque.
          </p>
        </div>
        <BotaoAdicionar topo onClick={() => setDaBase(true)}>
          Adicionar material
        </BotaoAdicionar>
        <AdicionarDaBaseDialog tipo="materiais" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => setCriando(true)} />
      </div>

      {criando && (
        <div className="mt-3 flex gap-1.5">
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nome.trim().length >= 2) salvar.mutate({ label: nome.trim() });
              if (e.key === "Escape") setCriando(false);
            }}
            placeholder="Ex: Zircônia HT"
            aria-label="Nome do novo material do cardápio"
            autoFocus
            className={`${inputCls} h-9 flex-none sm:w-80`}
          />
          <button
            type="button"
            disabled={nome.trim().length < 2 || ocupado}
            onClick={() => salvar.mutate({ label: nome.trim() })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white disabled:opacity-50"
          >
            {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Adicionar
          </button>
        </div>
      )}

      <CatalogoBusca
        valor={busca}
        onChange={setBusca}
        placeholder="Buscar material"
        achados={filtradas.length}
      />

      <div className="mt-3">
        <div className="mt-1 grid grid-cols-1 gap-4 md:grid-cols-2">
          {coluna(esquerda, "esquerda")}
          {coluna(direita, "direita")}
        </div>
      </div>

      {ofertas.length === 0 && !listaQuery.isLoading && (
        <p className="mt-2 text-[11px] text-zinc-500 dark:text-muted-foreground">
          Nenhum material no cardápio — o dentista não tem o que escolher. Crie o primeiro acima.
        </p>
      )}
    </div>
  );
}
